import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { SiYoutube, SiFacebook, SiInstagram, SiX, SiWhatsapp } from '@icons-pack/react-simple-icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { mockData } from '../mock';
import { useTheme } from './ThemeProvider';

const Footer = () => {
  const { theme } = useTheme();
  const { company } = mockData;
  const socialMedia = company.social_media;
  const location = useLocation();
  const navigate = useNavigate();

  const addr = company.address;
  const addressLines = [
    [addr.building, addr.street].filter(Boolean).join(", "),
    [addr.area, addr.city].filter(Boolean).join(", "),
    [addr.state, addr.zip, addr.country].filter(Boolean).join(", ")
  ].filter(line => line.length > 0);

  const formatAddress = () => {
    return (
      <>
        {addressLines.map((line, index) => (
          <React.Fragment key={index}>
            {line}
            {index < addressLines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    );
  };

  // Handle Home link click - navigate and scroll to top
  const handleHomeClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }, 50);
    }
  };

  // Handle Products link click - navigate and scroll to top
  const handleProductsClick = (e) => {
    e.preventDefault();
    navigate('/products');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 50);
  };

  // Handle About link click - navigate and scroll to top
  const handleAboutClick = (e) => {
    e.preventDefault();
    navigate('/about');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 50);
  };

  // Handle Contact link click - navigate and scroll to top
  const handleContactClick = (e) => {
    e.preventDefault();
    navigate('/contact');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 50);
  };

  // Handle Solutions/Scenarios click - navigate to home and scroll to section
  const handleSectionClick = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-black dark:bg-white border-t border-white/10 dark:border-black/10 text-white dark:text-black" data-testid="footer">
      <div className="container mx-auto px-4 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Section with Logo */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img
                src={theme === 'dark'
                  ? 'images/title/noBgBlack.png'
                  : 'images/title/noBgWhite.png'
                }
                alt="Sha by Mahadeva Systems"
                className="h-14 w-auto"
              />
              <div className="flex items-center border-l-2 h-12 dark:border-black border-white pl-4">
                <div className="flex flex-col justify-center">
                  <div className="text-xs text-white/60 dark:text-black/60">by</div>
                  <div className="text-sm font-semibold">{company.name}</div>
                </div>
              </div>
            </div>
            <p className="text-sm text-white/70 dark:text-black/70 leading-relaxed">
              {company.description}
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <a href="/" onClick={handleHomeClick} className="text-sm text-white/70 dark:text-black/70 hover:text-white dark:hover:text-black transition-colors cursor-pointer" data-testid="footer-home-link">
                  Home
                </a>
              </li>
              <li>
                <a href="/products" onClick={handleProductsClick} className="text-sm text-white/70 dark:text-black/70 hover:text-white dark:hover:text-black transition-colors cursor-pointer" data-testid="footer-products-link">
                  Products
                </a>
              </li>
              <li>
                <button onClick={() => handleSectionClick('solutions')} className="text-sm text-white/70 dark:text-black/70 hover:text-white dark:hover:text-black transition-colors">
                  Solutions
                </button>
              </li>
              <li>
                <button onClick={() => handleSectionClick('scenarios')} className="text-sm text-white/70 dark:text-black/70 hover:text-white dark:hover:text-black transition-colors">
                  Scenarios
                </button>
              </li>
              <li>
                <a href="/about" onClick={handleAboutClick} className="text-sm text-white/70 dark:text-black/70 hover:text-white dark:hover:text-black transition-colors cursor-pointer" data-testid="footer-about-link">
                  About
                </a>
              </li>
              <li>
                <a href="/contact" onClick={handleContactClick} className="text-sm text-white/70 dark:text-black/70 hover:text-white dark:hover:text-black transition-colors cursor-pointer" data-testid="footer-contact-link">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Contact Info</h3>
            <div className="space-y-3">
              <a href={`mailto:${company.email}`} className="flex items-start space-x-2 text-sm text-white/70 dark:text-black/70 hover:text-white dark:hover:text-black transition-colors">
                <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span className="break-all">{company.email}</span>
              </a>
              <a href={`tel:${company.phone}`} className="flex items-center space-x-2 text-sm text-white/70 dark:text-black/70 hover:text-white dark:hover:text-black transition-colors">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>{company.phone}</span>
              </a>
              <div className="flex items-start space-x-2 text-sm text-white/70 dark:text-black/70">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{formatAddress()}</span>
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Follow Us</h3>
            <div className="flex flex-wrap gap-3">
              <a href={socialMedia.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/20 dark:bg-black/20 flex items-center justify-center hover:bg-white dark:hover:bg-black hover:text-black dark:hover:text-white transition-all" aria-label="YouTube" data-testid="social-youtube">
                <SiYoutube className="w-5 h-5" />
              </a>
              <a href={socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/20 dark:bg-black/20 flex items-center justify-center hover:bg-white dark:hover:bg-black hover:text-black dark:hover:text-white transition-all" aria-label="Facebook" data-testid="social-facebook">
                <SiFacebook className="w-5 h-5" />
              </a>
              <a href={socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/20 dark:bg-black/20 flex items-center justify-center hover:bg-white dark:hover:bg-black hover:text-black dark:hover:text-white transition-all" aria-label="Instagram" data-testid="social-instagram">
                <SiInstagram className="w-5 h-5" />
              </a>
              <a href={socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/20 dark:bg-black/20 flex items-center justify-center hover:bg-white dark:hover:bg-black hover:text-black dark:hover:text-white transition-all" aria-label="Twitter" data-testid="social-twitter">
                <SiX className="w-5 h-5" />
              </a>
              <a href={socialMedia.whatsapp} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/20 dark:bg-black/20 flex items-center justify-center hover:bg-white dark:hover:bg-black hover:text-black dark:hover:text-white transition-all" aria-label="WhatsApp" data-testid="social-whatsapp">
                <SiWhatsapp className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 dark:border-black/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-white/60 dark:text-black/60">
              © {new Date().getFullYear()} {company.name}. All rights reserved.
            </p>
            <p className="text-sm text-white/60 dark:text-black/60">
              {company.tagline}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
